import { FC } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/*
  The icon layer — the fourth design axis, alongside type, colour and radius.

  Before this existed, icon geometry was invented at every call site: an audit
  found 12 distinct lucide `size={N}` values and 10 distinct raw <svg
  width/height> pairs. None of them tracked --text-scale, so the global density
  knob moved every word in the app and not one icon. RoomTypeBadge was the worst
  case — pinned at 15px box / 10px glyph, it rendered identically beside a
  13.4px list-row name and a 19.2px info-drawer title.

  Rungs live in packages/theme/tokens.css and are derived from type steps, so an icon scales
  with the text it sits next to.
*/
export type IconScale = "inline" | "field" | "control" | "nav";

const SCALE: Record<IconScale, string> = {
  inline: "var(--icon-inline)",
  field: "var(--icon-field)",
  control: "var(--icon-control)",
  nav: "var(--icon-nav)",
};

/*
  Stroke weight is expressed in the 24-unit viewBox, so it scales *with* the
  glyph — which means a small icon drawn at the same nominal width looks thinner
  than a large one. Each rung compensates, heaviest at the smallest size. These
  match what the codebase had already converged on by hand (2.25 on the badge,
  1.8 on info-row glyphs, 1.5 on header chevrons); they are now stated once.
*/
const STROKE: Record<IconScale, number> = {
  inline: 2.25,
  field: 1.9,
  control: 1.75,
  nav: 1.6,
};

/*
  Per-glyph optical correction.

  Lucide draws every glyph in a 24-unit box, but glyphs fill that box very
  differently, so equal `size` is not equal apparent size. Measured with
  getBBox() in a real browser — a naive scrape of the path `d` attribute gets
  this badly wrong, because paths use relative commands and report ChevronLeft
  as 2x2 units instead of its real 8x14.

  The number is each glyph's OPTICAL EXTENT: the geometric mean of its ink
  extents, including stroke, in viewBox units. Lucide's nominal fill is 20 of 24,
  so a glyph measuring 20 needs no correction. Anything absent defaults to 20 —
  i.e. uncorrected — so an unlisted glyph degrades to today's behaviour rather
  than to something wrong.
*/
const OPTICAL_EXTENT: Record<string, number> = {
  // Fills the box — correct slightly down.
  BadgeCheck: 22.0,
  Circle: 22.0,
  CircleAlert: 22.0,
  CircleCheck: 22.0,
  CircleX: 22.0,
  Compass: 22.0,
  EyeOff: 22.0,
  Info: 22.0,
  Layers: 22.0,
  MessagesSquare: 22.0,
  UserCircle2: 22.0,
  BellOff: 22.0,
  Edit2: 22.0,
  Pencil: 22.0,
  Paperclip: 21.7,
  Calendar: 21.0,
  Home: 21.0,
  Settings: 21.0,
  Trash2: 21.0,
  Lock: 21.0,
  CircleUser: 22.0,
  ImagePlus: 21.0,
  Bell: 21.0,
  Repeat: 21.0,
  UserPlus: 21.0,
  Users: 21.0,
  // Neutral.
  Loader2: 20.0,
  LogOut: 20.0,
  RotateCcw: 20.0,
  Search: 20.0,
  Sparkles: 20.0,
  Camera: 19.9,
  Flag: 19.9,
  Shield: 19.9,
  PencilLine: 19.6,
  // Under-fills the box — correct up.
  Redo2: 18.0,
  User: 17.9,
  VenetianMask: 17.6,
  Link2: 16.3,
  Plus: 16.0,
  Check: 15.3,
  ChevronsUpDown: 14.7,
  X: 14.0,
  ChevronDown: 10.6,
  ChevronLeft: 10.6,
  ChevronRight: 10.6,
};

const NOMINAL_FILL = 20;

/*
  Full geometric normalisation overcorrects: the eye compensates for shape only
  partly, so a chevron scaled the full 89% its extent implies looks oversized
  beside its label. Half compensation, clamped, is the standard treatment.
*/
const DAMPING = 0.5;
const MIN = 0.85;
const MAX = 1.35;

export const opticalScale = (glyphName?: string): number => {
  const extent = (glyphName && OPTICAL_EXTENT[glyphName]) || NOMINAL_FILL;
  const raw = NOMINAL_FILL / extent;
  const damped = 1 + (raw - 1) * DAMPING;
  return Math.min(MAX, Math.max(MIN, damped));
};

/*
  Renders a lucide glyph at a token rung, optically corrected.

  Use this instead of `<SomeIcon size={14} />`. ESLint blocks the raw numeric
  form outside components/ui — see eslint.config.cjs.
*/
export const Icon: FC<{
  glyph: LucideIcon;
  scale?: IconScale;
  /** Override stroke weight; defaults to the rung's compensated value. */
  strokeWidth?: number;
  className?: string;
}> = ({ glyph: Glyph, scale = "control", strokeWidth, className }) => {
  const factor = opticalScale((Glyph as { displayName?: string }).displayName);
  return (
    <Glyph
      aria-hidden
      strokeWidth={strokeWidth ?? STROKE[scale]}
      className={cn("shrink-0", className)}
      style={{
        width: `calc(${SCALE[scale]} * ${factor})`,
        height: `calc(${SCALE[scale]} * ${factor})`,
      }}
    />
  );
};
